import React, { useState, useEffect, useRef } from "react";
import firebase from "../../firebase";
import { getVendorId } from "../../utils/rbac";
import "./vendorprofile.css";

const db      = () => firebase.firestore();
const storage = () => firebase.storage();

// ── Helpers ───────────────────────────────────────────────────────────────
const MAX_WORK_IMAGES = 10;
const MAX_FILE_MB     = 2;

const resizeImage = (file, maxDim = 1024, quality = 0.82) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round((height * maxDim) / width); width = maxDim; }
          else                { width  = Math.round((width  * maxDim) / height); height = maxDim; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

// ── Section Card ──────────────────────────────────────────────────────────
const Section = ({ icon, title, children, extra }) => (
  <div className="vp_card">
    <div className="vp_section_header">
      <i className={`fa ${icon}`}></i> {title}
      {extra && <div className="vp_section_extra">{extra}</div>}
    </div>
    {children}
  </div>
);

// ── Info Row (read view) ──────────────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
  <div className="vp_info_row">
    <span className="vp_info_icon"><i className={`fa ${icon}`}></i></span>
    <div>
      <div className="vp_info_label">{label}</div>
      <div className="vp_info_value">{value || <span className="vp_empty_val">—</span>}</div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Main VendorProfile
// ═══════════════════════════════════════════════════════════════════════════
const VendorProfile = () => {
  const vendorId = getVendorId();

  // ── Profile state ────────────────────────────────────────────────────────
  const [vendor,      setVendor]      = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [editing,     setEditing]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [toast,       setToast]       = useState(null);
  const [form,        setForm]        = useState({});
  const [avatarFile,  setAvatarFile]  = useState(null);   // File | null
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef();

  // ── Work images state ────────────────────────────────────────────────────
  const [workUrls,      setWorkUrls]      = useState([]);
  const [pendingFiles,  setPendingFiles]  = useState([]);  // File[]
  const [loadingWork,   setLoadingWork]   = useState(true);
  const [uploadingWork, setUploadingWork] = useState(false);
  const [deletingIdx,   setDeletingIdx]   = useState(null);
  const workInputRef = useRef();

  // ── Toast helper ─────────────────────────────────────────────────────────
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Load vendor profile ──────────────────────────────────────────────────
  useEffect(() => {
    if (!vendorId) { setLoading(false); return; }
    db().collection("vendors").doc(vendorId).get()
      .then((snap) => {
        if (snap.exists) {
          const data = { uid: snap.id, ...snap.data() };
          setVendor(data);
          setForm({
            name:        data.name        || "",
            email:       data.email       || "",
            phone:       data.phoneNumber || data.phone || "",
            salonName:   data.salonName   || "",
            website:     data.website     || "",
            about:       data.about       || "",
            homeService: !!data.homeService,
          });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId]);

  // ── Load work images ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!vendor) return;
    const code = vendor.vendorCode || vendorId;
    setLoadingWork(true);
    storage().ref(`SalonImageCollection/${code}`).listAll()
      .then(async (result) => {
        const urls = await Promise.all(result.items.map((ref) => ref.getDownloadURL()));
        setWorkUrls(urls);
        setLoadingWork(false);
      })
      .catch(() => setLoadingWork(false));
  }, [vendor, vendorId]);

  // ── Handle avatar pick ───────────────────────────────────────────────────
  const handleAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  // ── Save profile ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim())      { showToast("Name is required.", "error"); return; }
    if (!form.salonName.trim()) { showToast("Salon name is required.", "error"); return; }

    setSaving(true);
    try {
      let vendorImage = vendor?.vendorImage || "";

      // Upload avatar if changed
      if (avatarFile) {
        const blob = await resizeImage(avatarFile, 400, 0.8);
        const ref  = storage().ref(`vendorImages/${form.name.trim()}_${Date.now()}.jpg`);
        await ref.put(blob);
        vendorImage = await ref.getDownloadURL();
      }

      await db().collection("vendors").doc(vendorId).update({
        name:        form.name,
        email:       form.email,
        phoneNumber: form.phone,
        salonName:   form.salonName,
        website:     form.website,
        about:       form.about,
        homeService: form.homeService,
        vendorImage,
      });

      setVendor((v) => ({ ...v, ...form, vendorImage, phoneNumber: form.phone }));
      setAvatarFile(null);
      setAvatarPreview(null);
      setEditing(false);
      showToast("Profile updated successfully!");
    } catch (err) {
      showToast("Failed to update profile: " + err.message, "error");
    }
    setSaving(false);
  };

  // ── Pick pending work images ─────────────────────────────────────────────
  const handleWorkPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    const total     = workUrls.length + pendingFiles.length + files.length;
    const remaining = MAX_WORK_IMAGES - workUrls.length - pendingFiles.length;

    if (remaining <= 0) {
      showToast(`Maximum ${MAX_WORK_IMAGES} images allowed. Delete some first.`, "error"); return;
    }

    const toAdd = files.slice(0, remaining);
    const oversized = toAdd.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized.length) {
      showToast(`${oversized.length} file(s) exceed ${MAX_FILE_MB}MB and were skipped.`, "error");
    }
    setPendingFiles((prev) => [...prev, ...toAdd.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024)]);
  };

  // ── Remove pending (not-yet-uploaded) image ──────────────────────────────
  const removePending = (i) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== i));
  };

  // ── Upload pending work images ───────────────────────────────────────────
  const handleWorkUpload = async () => {
    if (!pendingFiles.length) return;
    setUploadingWork(true);
    const code    = vendor?.vendorCode || vendorId;
    const baseRef = storage().ref(`SalonImageCollection/${code}`);
    const newUrls = [];

    for (let i = 0; i < pendingFiles.length; i++) {
      try {
        const blob = await resizeImage(pendingFiles[i], 1024, 0.82);
        const ref  = baseRef.child(`salon_image_${Date.now()}_${i}.jpg`);
        await ref.put(blob);
        newUrls.push(await ref.getDownloadURL());
      } catch (err) {
        showToast(`Image ${i + 1} failed: ${err.message}`, "error");
      }
    }

    setWorkUrls((prev) => [...prev, ...newUrls]);
    setPendingFiles([]);
    setUploadingWork(false);
    showToast(`${newUrls.length} image(s) uploaded!`);
  };

  // ── Delete uploaded work image ────────────────────────────────────────────
  const handleWorkDelete = async (url, i) => {
    setDeletingIdx(i);
    try {
      await storage().refFromURL(url).delete();
      setWorkUrls((prev) => prev.filter((_, idx) => idx !== i));
      showToast("Image deleted.");
    } catch (err) {
      showToast("Delete failed: " + err.message, "error");
    }
    setDeletingIdx(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!vendorId) return <div className="vp_page"><div className="vp_empty">Vendor ID not found. Please log in again.</div></div>;
  if (loading)   return <div className="vp_page"><div className="vp_loading"><i className="fa fa-spinner fa-pulse fa-2x"></i></div></div>;
  if (!vendor)   return <div className="vp_page"><div className="vp_empty">Vendor profile not found.</div></div>;

  const avatarSrc = avatarPreview || vendor.vendorImage || null;

  return (
    <div className="vp_page">

      {/* Toast */}
      {toast && (
        <div className={`vp_toast vp_toast_${toast.type}`}>
          <i className={`fa ${toast.type === "success" ? "fa-check-circle" : "fa-exclamation-circle"}`}></i>
          {toast.msg}
        </div>
      )}

      {/* ── Hero banner ── */}
      <div className="vp_hero">
        <div className="vp_hero_bg" style={vendor.shopImage ? { backgroundImage: `url(${vendor.shopImage})` } : {}}></div>
        <div className="vp_hero_overlay"></div>
        <div className="vp_hero_content">
          <div className="vp_avatar_wrap">
            <div className="vp_avatar">
              {avatarSrc
                ? <img src={avatarSrc} alt={vendor.name} />
                : <span>{(vendor.name || "?")[0].toUpperCase()}</span>}
            </div>
            {editing && (
              <>
                <button className="vp_avatar_edit_btn" onClick={() => avatarInputRef.current?.click()}>
                  <i className="fa fa-camera"></i>
                </button>
                <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatarPick} />
              </>
            )}
          </div>
          <div className="vp_hero_info">
            <div className="vp_hero_name">{vendor.salonName || vendor.name}</div>
            <div className="vp_hero_sub">
              <i className="fa fa-user"></i> {vendor.name}
              &nbsp;&nbsp;
              <i className="fa fa-phone"></i> {vendor.phoneNumber || vendor.phone || "—"}
            </div>
            {vendor.homeService && (
              <span className="vp_home_service_pill"><i className="fa fa-home"></i> Home Service</span>
            )}
          </div>
          {!editing
            ? <button className="vp_edit_btn" onClick={() => setEditing(true)}><i className="fa fa-pencil"></i> Edit Profile</button>
            : <div className="vp_edit_actions">
                <button className="vp_save_btn" onClick={handleSave} disabled={saving}>
                  {saving ? <i className="fa fa-spinner fa-pulse"></i> : <i className="fa fa-check"></i>}
                  {saving ? " Saving…" : " Save"}
                </button>
                <button className="vp_cancel_btn" onClick={() => { setEditing(false); setAvatarFile(null); setAvatarPreview(null); }}>
                  <i className="fa fa-times"></i> Cancel
                </button>
              </div>
          }
        </div>
      </div>

      {/* ── Profile details ── */}
      {editing ? (
        /* EDIT FORM */
        <Section icon="fa-user-circle" title="Edit Profile">
          <div className="vp_form_grid">
            <div className="vp_form_group">
              <label>Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="vp_form_group">
              <label>Email</label>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email" />
            </div>
            <div className="vp_form_group">
              <label>Phone <span className="vp_readonly_tag">read-only</span></label>
              <input value={form.phone} readOnly className="vp_readonly" />
            </div>
            <div className="vp_form_group">
              <label>Salon Name</label>
              <input value={form.salonName} onChange={(e) => setForm({ ...form, salonName: e.target.value })} placeholder="Salon Name" />
            </div>
            <div className="vp_form_group vp_full">
              <label>Website</label>
              <input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.example.com" />
            </div>
            <div className="vp_form_group vp_full">
              <label>About</label>
              <textarea rows={4} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="Tell customers about your salon…" />
            </div>
            <div className="vp_form_group vp_full">
              <label className="vp_toggle_label">
                <span>Home Service</span>
                <div
                  className={`vp_toggle${form.homeService ? " on" : ""}`}
                  onClick={() => setForm({ ...form, homeService: !form.homeService })}
                >
                  <div className="vp_toggle_knob"></div>
                </div>
              </label>
            </div>
          </div>
        </Section>
      ) : (
        /* READ VIEW */
        <Section icon="fa-info-circle" title="Salon Details"
          extra={
            <button className="vp_inline_edit_btn" onClick={() => setEditing(true)}>
              <i className="fa fa-pencil"></i> Edit
            </button>
          }
        >
          <div className="vp_info_grid">
            <InfoRow icon="fa-user"         label="Owner Name"  value={vendor.name} />
            <InfoRow icon="fa-envelope"     label="Email"       value={vendor.email} />
            <InfoRow icon="fa-phone"        label="Phone"       value={vendor.phoneNumber || vendor.phone} />
            <InfoRow icon="fa-scissors"     label="Salon Name"  value={vendor.salonName} />
            <InfoRow icon="fa-globe"        label="Website"     value={vendor.website} />
            <InfoRow icon="fa-home"         label="Home Service" value={vendor.homeService ? "Yes — available" : "Not available"} />
          </div>
          {vendor.about && (
            <div className="vp_about_box">
              <div className="vp_about_label"><i className="fa fa-align-left"></i> About</div>
              <div className="vp_about_text">{vendor.about}</div>
            </div>
          )}
        </Section>
      )}

      {/* ── Work Images / Portfolio ── */}
      <Section
        icon="fa-picture-o"
        title={`Work Portfolio (${workUrls.length + pendingFiles.length} / ${MAX_WORK_IMAGES})`}
        extra={
          <button
            className="vp_add_img_btn"
            onClick={() => workInputRef.current?.click()}
            disabled={workUrls.length + pendingFiles.length >= MAX_WORK_IMAGES}
          >
            <i className="fa fa-plus"></i> Add Images
          </button>
        }
      >
        <input
          ref={workInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleWorkPick}
        />

        {loadingWork ? (
          <div className="vp_loading" style={{ padding: "40px 0" }}>
            <i className="fa fa-spinner fa-pulse"></i>
          </div>
        ) : workUrls.length === 0 && pendingFiles.length === 0 ? (
          <div className="vp_work_empty">
            <i className="fa fa-image" style={{ fontSize: 32, color: "#e0e0e0" }}></i>
            <div>No portfolio images yet. Click <strong>Add Images</strong> to upload.</div>
            <div className="vp_work_hint">Max {MAX_WORK_IMAGES} images · {MAX_FILE_MB}MB per file</div>
          </div>
        ) : (
          <div className="vp_work_grid">
            {/* Already-uploaded images */}
            {workUrls.map((url, i) => (
              <div key={url} className="vp_work_thumb">
                <img src={url} alt={`work-${i}`} />
                <div className="vp_work_badge uploaded"><i className="fa fa-check-circle"></i> Uploaded</div>
                <button
                  className="vp_work_del_btn"
                  onClick={() => handleWorkDelete(url, i)}
                  disabled={deletingIdx === i}
                  title="Delete"
                >
                  {deletingIdx === i
                    ? <i className="fa fa-spinner fa-pulse"></i>
                    : <i className="fa fa-times"></i>}
                </button>
              </div>
            ))}

            {/* Pending (not yet uploaded) images */}
            {pendingFiles.map((file, i) => (
              <div key={i} className="vp_work_thumb pending">
                <img src={URL.createObjectURL(file)} alt={`pending-${i}`} />
                <div className="vp_work_badge pending"><i className="fa fa-clock-o"></i> Pending</div>
                <button className="vp_work_del_btn" onClick={() => removePending(i)} title="Remove">
                  <i className="fa fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Upload pending button */}
        {pendingFiles.length > 0 && (
          <div className="vp_upload_row">
            <span className="vp_pending_count">
              {pendingFiles.length} image{pendingFiles.length !== 1 ? "s" : ""} pending upload
            </span>
            <button
              className="vp_upload_btn"
              onClick={handleWorkUpload}
              disabled={uploadingWork}
            >
              {uploadingWork
                ? <><i className="fa fa-spinner fa-pulse"></i> Uploading…</>
                : <><i className="fa fa-upload"></i> Upload Now</>}
            </button>
          </div>
        )}
      </Section>
    </div>
  );
};

export default VendorProfile;
